import { getExtraHours, calcExtraHoursTotal } from "./extraHoursBilling";
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import "./ReportesMobile.css";

/* ================== Axios base (igual que Reportes.js) ================== */
Axios.defaults.baseURL = "https://backend.sansfrontieres-design.com";
Axios.defaults.withCredentials = false;

// Mismo filtro de resumen que en PC (Reportes.js)
const EXCLUDED_STATUS_FOR_XLS = new Set(["billing", "payed", "archived", "pre-archived"]);
const filterOutExcludedXLS = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => {
    const s = String(r?.jobsta ?? "").trim().toLowerCase();
    return !EXCLUDED_STATUS_FOR_XLS.has(s);
  });

/* ================= Helpers (copiados de Reportes.js) ================= */
const normalize = (v) => (v ?? "").toString().toLowerCase().replace(/[\s-]+/g, "");

// Devuelve fecha local YYYY-MM-DD a partir de un Date o string tipo 'YYYY-MM-DD HH:mm:ss'
function getDateKey(value) {
  if (!value) return "";
  if (value instanceof Date && !isNaN(value)) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const dt = new Date(value);
  if (!isNaN(dt)) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : s.slice(0, 10);
}

const n = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return isNaN(x) ? 0 : x;
};
const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Equivalencias de Type para cálculos de reportes.
// Solo cambia cómo se interpreta el Type al calcular; no altera el valor guardado en BD.
const normalizeCalcJobType = (value) => {
  const t = normalize(value);
  switch (t) {
    case "coaxbentleyasb": return "coaxasbuilt";
    case "coaxbentleydes": return "coaxdesign";
    case "fiberbentleyasb": return "fiberasbuiltbau";
    case "fiberbentleydes": return "fiberasbuiltbau";
    default: return t;
  }
};


// Types que NO cobran LODE base en la fórmula genérica de Main/Main-SFD.
// Además, el recargo genérico por LODE > 2000 fue eliminado para TODOS los Types.
const NO_GENERIC_LODE_BASE_TYPES = new Set([
  "coaxasbuilt",
  "coaxdesign",
  "coaxbentleyasb",
  "coaxbentleydes",
  "fiberbentleyasb",
  "fiberbentleydes",
  "route",
  "routefttp",
  "fiberfttp",
]);

const usesGenericLodeBase = (r) => !NO_GENERIC_LODE_BASE_TYPES.has(normalize(r?.jobti));

const isBentleySplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitcoaxasbuilt";
const isBentleySplitFiberAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitfiberasbuilt";

const isProject01 = (r) => String(r?.jobpro ?? "").trim() === "Project-01";
const isFiberDesignBAU = (r) => normalize(r?.jobti) === "fiberdesignbau";
const isFiberAsbuiltBAU = (r) => {
  const t = normalize(r?.jobti);
  return t === "fiberasbuiltbau" || t === "fiberbentleyasb";
};
const isCoaxAsbuilt = (r) => {
  const t = normalize(r?.jobti);
  return t === "coaxasbuilt" || t === "coaxbentleyasb";
};

// Route / Route FTTP: SOLO procesan Route.
// NO Coax, NO Asbuilt, NO LODE, NO HC.
const isRouteOnlyType = (r) => {
  const t = normalize(r?.jobti);
  return t === "route" || t === "routefttp";
};

const isCoaxAsbRevertSplit = (r) => normalize(r?.jobti) === "coaxasbrevertsplit";
const isCoaxAsbRevertN0 = (r) => normalize(r?.jobti) === "coaxasbrevertn+0";
const isIrisSplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "irissplitcoaxasbuilt";

// Coax Asb Revert Split - regla nueva:
// Main = 160 fijo.
// Main-SFD = 12.5 + $0.50 por cada HC/AMP por encima de 20.
const coaxAsbRevertSplitSfdTotal = (hc) => {
  const value = n(hc);
  return r2(12.5 + Math.max(0, value - 20) * 0.5);
};

// Main: devuelve AL (suma AC..AK) según modo
// Add the separately priced hours exactly once, preserving every base-price rule.
function calcMainAL(r, mode) {
  return r2(calcMainBase(r, mode) + calcExtraHoursTotal(r, mode === "ADV" ? "SFD" : "CLIENT"));
}
function calcBentleyTotal(r, mode) {
  return r2(calcBentleyBase(r, mode) + calcExtraHoursTotal(r, mode === "BENTLEY_SFD" ? "SFD" : "CLIENT"));
}

function calcMainBase(r, mode /* "SFD" | "ADV" */) {
  const routeA = n(r?.jobrouteaerial);
  const routeU = n(r?.jobrouteug);
  const coaxA = n(r?.jobcoaxae);
  const coaxU = n(r?.jobcoaxug);
  const asbA = n(r?.jobasbae);
  const asbU = n(r?.jobasbug);
  const lode = n(r?.joblode);
  const hc = n(r?.jobhc);
  const jobType = normalizeCalcJobType(r?.jobti);

  // Reglas específicas de Coax Asb Revert.
  // Se evalúan antes de Project-01 porque sus precios son propios y no dependen de la regla genérica.
  if (isCoaxAsbRevertSplit(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(160);
  }

  // Coax Asb Revert N+0: precio total fijo, sin condiciones ni componentes adicionales.
  if (isCoaxAsbRevertN0(r)) {
    if (mode === "ADV") return r2(10);
    return r2(20);
  }

  // Iris Split Coax Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = misma regla que Coax Asb Revert Split.
  if (isIrisSplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(105);
  }

  if (mode === "ADV" && isProject01(r)) return 0;

  // Route / Route FTTP - regla exclusiva de Route:
  // MAIN: SOLO Route(Main). NO Coax, NO Asbuilt, NO LODE, NO HC.
  // MAIN-SFD: SOLO Route(Main-SFD). NO Coax, NO Asbuilt, NO LODE, NO HC.
  if (isRouteOnlyType(r)) {
    const totalRoute = routeA + routeU;

    if (mode === "ADV") {
      if (totalRoute > 20000) return r2(totalRoute * 0.008);
      let routeTotal = totalRoute > 0 ? r2(10) : 0;
      if (totalRoute > 2000) routeTotal += r2(((totalRoute - 2000) / 5280) * 12.5);
      return r2(routeTotal);
    }

    if (totalRoute > 20000) return r2(totalRoute * 0.008);
    let routeTotal = totalRoute > 0 ? r2(30.25) : 0;
    if (totalRoute > 2000) routeTotal += r2((totalRoute - 2000) * 0.008);
    return r2(routeTotal);
  }

  // Coax Asbuilt / Coax Bentley Asb - regla vigente:
  // MAIN: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  // MAIN-SFD: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  if (isCoaxAsbuilt(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "ADV") {
      const asbuiltBase = totalAsb > 0 ? r2(8.5) : 0;
      const asbuiltGt2000 = totalAsb > 2000
        ? r2(((totalAsb - 2000) / 5280) * 10)
        : 0;
      return r2(asbuiltBase + asbuiltGt2000);
    }

    const asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    const asbuiltGt2000 = totalAsb > 2000
      ? r2((totalAsb - 2000) * 0.014)
      : 0;
    return r2(asbuiltBase + asbuiltGt2000);
  }

  // Fiber Design BAU - reglas especiales:
  // MAIN: 55.75 + Route(Main) + LODE/Nodes*14.25 + HC/Amps*16.25 + Coax>2000*0.019
  // MAIN-SFD: 12.50 + Route(Main-SFD) + LODE*5 + HC*5 + $10 por milla de Coax sobre 2000'
  if (isFiberDesignBAU(r)) {
    const totalRoute = routeA + routeU;
    const totalCoax = coaxA + coaxU;
    const coaxGt2000Component = totalCoax > 2000
      ? (mode === "SFD"
          ? r2((totalCoax - 2000) * 0.019)
          : r2(((totalCoax - 2000) / 5280) * 10))
      : 0;
    let routeComponent = 0;

    if (mode === "SFD") {
      if (totalRoute > 20000) {
        routeComponent = r2(totalRoute * 0.008);
      } else {
        if (totalRoute > 0) routeComponent += r2(30.25);
        if (totalRoute > 2000) routeComponent += r2((totalRoute - 2000) * 0.008);
      }

      return r2(
        55.75 +
        routeComponent +
        (lode * 14.25) +
        (hc * 16.25) +
        coaxGt2000Component
      );
    }

    // ADV / Main-SFD
    if (totalRoute > 20000) {
      routeComponent = r2(totalRoute * 0.008);
    } else {
      if (totalRoute > 0) routeComponent += r2(10);
      if (totalRoute > 2000) {
        routeComponent += r2(((totalRoute - 2000) / 5280) * 12.5);
      }
    }

    return r2(
      12.5 +
      routeComponent +
      (lode * 5) +
      (hc * 5) +
      coaxGt2000Component
    );
  }

  // Fiber Asbuilt BAU / Fiber Bentley Asb - regla vigente:
  // MAIN: 25 + HC/Amps*16.25 + Asbuilt>2000 * 0.014
  // MAIN-SFD: 15 + HC/Amps*5 + $10 por milla de Asbuilt sobre 2000'
  // NO considera Route, Coax ni LODE.
  if (isFiberAsbuiltBAU(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "SFD") {
      const asbuiltGt2000 = totalAsb > 2000
        ? r2((totalAsb - 2000) * 0.014)
        : 0;
      return r2(
        25 +
        (hc * 16.25) +
        asbuiltGt2000
      );
    }

    const asbuiltGt2000 = totalAsb > 2000
      ? r2(((totalAsb - 2000) / 5280) * 10)
      : 0;
    return r2(
      15 +
      (hc * 5) +
      asbuiltGt2000
    );
  }

  // Bentley Split Coax Asbuilt: Main = 105; Main-SFD usa la misma lógica de Iris-Splits.
  if (isBentleySplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return calcBentleyBase(r, "BENTLEY_SFD");
    return r2(105);
  }

  // Bentley Split Fiber Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = regla de Fiber Iris SOLO para Main-SFD.
  if (isBentleySplitFiberAsbuilt(r)) {
    if (mode === "ADV") {
      return r2(30 + Math.max(0, lode - 1) * 15);
    }
    return r2(105);
  }

  if (jobType === "fiberiris" || jobType === "irissplitfiberdesign" || jobType === "irissplitfiberasbuilt" || jobType === "bentleysplitfiberdesign") {
    if (lode <= 0) return 0;
    if (mode === "ADV") return r2(30 + Math.max(0, lode - 1) * 15);
    return r2(60 + Math.max(0, lode - 1) * 33);
  }

  const totalRoute = routeA + routeU;
  const totalCoax = coaxA + coaxU;
  const totalAsb = asbA + asbU;

  let totalStrand = 0;      // AC
  let strandGt2000 = 0;     // AD
  let strandGt20000 = 0;    // AE
  let totalDesign = 0;      // AF
  let designGt2000 = 0;     // AG
  let lodeBase = 0;         // AH
  let lodeGt2000 = 0;       // AI
  let asbuiltBase = 0;      // AJ
  let asbuiltGt2000 = 0;    // AK

  if (mode === "ADV") {
    if (totalRoute > 0) totalStrand = r2(10);
    if (totalCoax > 0) totalDesign = r2(12.5);
    if (totalAsb > 0) asbuiltBase = r2(8.5);
    if (lode > 0 && usesGenericLodeBase(r)) lodeBase = r2(8.5);

    if (totalRoute > 2000) strandGt2000 = r2(((totalRoute - 2000) / 5280) * 12.5);

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    if (totalCoax > 2000) designGt2000 = r2(((totalCoax - 2000) / 5280) * 15.5);
    if (totalAsb > 2000) asbuiltGt2000 = r2(((totalAsb - 2000) / 5280) * 10);
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.
  } else {
    totalStrand = totalRoute > 0 ? r2(30.25) : 0;
    strandGt2000 = totalRoute > 2000 ? r2((totalRoute - 2000) * 0.008) : 0;
    strandGt20000 = 0;

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    totalDesign = totalCoax > 0 ? r2(36.25) : 0;
    designGt2000 = totalCoax > 2000 ? r2((totalCoax - 2000) * 0.014) : 0;

    lodeBase = (lode > 0 && usesGenericLodeBase(r)) ? r2(15) : 0;
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.

    asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    asbuiltGt2000 = totalAsb > 2000 ? r2((totalAsb - 2000) * 0.014) : 0;
  }

  return r2(
    totalStrand +
      strandGt2000 +
      strandGt20000 +
      totalDesign +
      designGt2000 +
      lodeBase +
      lodeGt2000 +
      asbuiltBase +
      asbuiltGt2000
  );
}

// Coax Bentley: devuelve TOTAL (columna AC/TOTAL) según modo
function calcBentleyBase(r, mode /* "SFD" | "BENTLEY_SFD" */) {
  const lode = n(r?.joblode);

  if (mode === "BENTLEY_SFD") {
    if (isProject01(r)) return 0;

    const project = String(r?.jobpro ?? "");
    const rawW = r?.joblode;
    const rawX = r?.jobhc;

    const isBlankW = rawW === "" || rawW === null || rawW === undefined;
    if (isBlankW) return 0;

    const W = parseFloat(String(rawW).replace(",", "."));
    const Xs = String(rawX ?? "");

    const isXEmptyOrZeroOrDash = Xs === "" || Xs === "-" || Xs === "0" || Xs === "0.0";

    const X = parseFloat(String(rawX ?? "").replace(",", "."));
    const xNum = isNaN(X) ? 0 : X;

    const calcFromX = () => {
      if (isXEmptyOrZeroOrDash) return 15;
      if (xNum <= 14) return 30;
      if (xNum <= 24) return 45;
      return 60;
    };

    if (project === "Project-02" || project === "Project-03") {
      if (W === 2) return 60;
      if (W === 1) return calcFromX();
      return 0;
    }

    if (W >= 1) return calcFromX();
    return 0;
  }

  // SFD (viejo)
  if (lode === 0) return 0;
  if (lode === 1) return 125 + 55;
  if (lode > 1) return (125 * lode) + 55;
  return 0;
}


const PROJECTS = [
  "Project-01","Project-02","Project-03","Project-04",
  "Project-05","Project-06","Project-07","Project-08"
];

export default function ReportesMobile() {
  // Fecha a procesar (para "Por Project")
  const [processDate, setProcessDate] = useState(() => getDateKey(new Date()));

  // Gate por password (mantiene sesión en refresh/F5, pero no tras logout)
  const SESSION_KEY = "reportes_mobile_unlocked";
  const [isUnlocked, setIsUnlocked] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });

  // data base
  const [projectsRows, setProjectsRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const PASSWORD = "Invoicexd";

  const handleUnlock = () => {
    const p = window.prompt("Ingrese password:");
    if (p === null) return;
    if (String(p) === PASSWORD) {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {}
      setIsUnlocked(true);
    } else {
      window.alert("Password incorrecto.");
    }
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {}
    setIsUnlocked(false);
    setProjectsRows([]);
    setErr("");
    setLoading(false);
  };


  useEffect(() => {
  // No cargar nada mientras esté bloqueado
  if (!isUnlocked) return;

  let alive = true;

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
      if (!alive) return;

      if (data?.status === "success") {
        const arr = Array.isArray(data.data) ? data.data : [];
        setProjectsRows(arr);
      } else {
        setErr("No se pudo cargar Projects.");
      }
    } catch (e) {
      if (!alive) return;
      setErr("Error de conexión al cargar Projects.");
    } finally {
      if (!alive) return;
      setLoading(false);
    }
  };

  load();
  return () => { alive = false; };
}, [isUnlocked]);


  // En la imagen están activos los 4 conceptos
  const flags = useMemo(() => ({
    main: true,
    bentley: true,
    mainSFD: true,
    bentleySFD: true
  }), []);

  const isCoaxBentley = (r) => {
    const jobti = normalize(r?.jobti);
    return jobti === "coaxbentley" || jobti === "irissplits" || jobti === "irissplitcoaxdesign" || jobti === "bentleysplitcoaxdesign";
  };

  // Base de cálculo idéntica a PC: se filtra al momento de calcular, no al cargar.
  const summaryBaseRows = useMemo(() => filterOutExcludedXLS(projectsRows), [projectsRows]);

  const totalFinalForRows = (rows) => {
    const rowsBentley = rows.filter((r) => isCoaxBentley(r));
    const rowsMain = rows.filter((r) => !isCoaxBentley(r));

    const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
    const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
    const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
    const sumBentleySFD = flags.bentleySFD
      ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
      : 0;

    const baseTotal = r2(sumMain + sumBentley);
    const sfdTotal = r2(sumMainSFD + sumBentleySFD);
    const hasBaseSelection = !!(flags.main || flags.bentley);
    const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

    return totalFinal;
  };

  // ======= TABLA 1: Resumen (igual que Reportes.js) =======
  const resumen = useMemo(() => {
    const arr = summaryBaseRows;

    const rowsBentley = arr.filter((r) => isCoaxBentley(r));
    const rowsMain = arr.filter((r) => !isCoaxBentley(r));

    const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
    const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
    const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
    const sumBentleySFD = flags.bentleySFD
      ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
      : 0;

    const baseTotal = r2(sumMain + sumBentley);
    const sfdTotal = r2(sumMainSFD + sumBentleySFD);
    const hasBaseSelection = !!(flags.main || flags.bentley);
    const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

    const rows = [];
    if (flags.main) rows.push({ concepto: "Main", total: sumMain });
    if (flags.bentley) rows.push({ concepto: "Coax Bentley", total: sumBentley });
    if (flags.mainSFD) rows.push({ concepto: "Main-SFD", total: sumMainSFD });
    if (flags.bentleySFD) rows.push({ concepto: "Coax Bentley SFD", total: sumBentleySFD });

    const rowsFiltered = rows.filter((r) => Number(r.total || 0) > 0);

    return { rows: rowsFiltered, totalFinal };
  }, [summaryBaseRows, flags]);

  
// ======= NUEVA TABLA: Totales por Project (Main SFD + Coax Bentley SFD) =======
const totalesSFDPorProject = useMemo(() => {
  const arr = summaryBaseRows;

  const rows = PROJECTS.map((jobpro) => {
    const rowsP = arr.filter((r) => String(r?.jobpro ?? "").trim() === jobpro);

    const totalMainSFD = r2(
      rowsP
        .filter((r) => !isCoaxBentley(r))
        .reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)
    );

    const totalBentleySFD = r2(
      rowsP
        .filter((r) => isCoaxBentley(r))
        .reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0)
    );

    const total = r2(totalMainSFD + totalBentleySFD);
    return { jobpro, total };
  });

  const rowsFiltered = rows.filter((r) => Number(r.total || 0) > 0);
  const grandTotal = r2(rowsFiltered.reduce((acc, r) => acc + Number(r.total || 0), 0));
  return { rows: rowsFiltered, grandTotal };
}, [summaryBaseRows]);

  // ======= TABLA 2: TOTAL FINAL por día (usa jobdatesub, igual que PC) =======
  const porDia = useMemo(() => {
    const arr = summaryBaseRows;

    const map = new Map();
    arr.forEach((r) => {
      const k = getDateKey(r?.jobdatesub);
      if (!k) return;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });

    const rows = Array.from(map.entries())
      .map(([day, rowsDay]) => ({ day, total: totalFinalForRows(rowsDay) }))
      .sort((a, b) => String(b.day).localeCompare(String(a.day)));

    const rowsFiltered = rows.filter((r) => Number(r.total || 0) > 0);

    const grandTotal = r2(rowsFiltered.reduce((acc, r) => acc + Number(r.total || 0), 0));

    return { rows: rowsFiltered, grandTotal };
  }, [summaryBaseRows, flags]);

  // ======= TABLA 3: Por Project (usa processDate + jobdatesub, igual que PC) =======
  const porProject = useMemo(() => {
    const arr = summaryBaseRows;
    const targetKey = processDate || getDateKey(new Date());

    const todaysRows = arr.filter((r) => getDateKey(r?.jobdatesub) === targetKey);

    const map = new Map(PROJECTS.map((p) => [p, []]));
    todaysRows.forEach((r) => {
      const k = String(r?.jobpro ?? "").trim();
      if (!k) return;
      if (!map.has(k)) return;
      map.get(k).push(r);
    });

    const rows = PROJECTS.map((jobpro) => {
      const rowsP = map.get(jobpro) || [];
      return { jobpro, count: rowsP.length, total: totalFinalForRows(rowsP) };
    });

    const rowsFiltered = rows.filter((r) => Number(r.total || 0) > 0);

    const dayTotal = r2(rowsFiltered.reduce((acc, r) => acc + Number(r.total || 0), 0));
    const dayCount = rowsFiltered.reduce((acc, r) => acc + Number(r.count || 0), 0);

    return { dateKey: targetKey, rows: rowsFiltered, dayTotal, dayCount };
  }, [summaryBaseRows, flags, processDate]);

  const fmt = (x) =>
    (Number(x) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
    <div className="rm-page">
      <div className="rm-wrap">
      <div className="rm-topbar">
        <div className="rm-brand">
          <img src={(process.env.PUBLIC_URL || "") + "/img/firma.png"} alt="SFD" />
        </div>
        <div className="rm-topbar-actions" style={{ display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={handleUnlock}
            style={{
              width: "56px",
              height: "40px",
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.25)",
              cursor: "pointer"
            }}
            title="Ingresar"
          >
            🔑
          </button>

          <button
            type="button"
            onClick={handleLogout}
            style={{
              width: "56px",
              height: "40px",
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.25)",
              cursor: "pointer"
            }}
            title="Cerrar sesión"
          >
            ⎋
          </button>
        </div>
      </div>

      {!isUnlocked ? (
        <div
          style={{
            padding: "18px",
            borderRadius: "18px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "rgba(255,255,255,0.85)"
          }}
        >
          Ingrese el password con el botón 🔑 para ver los reportes.
        </div>
      ) : (
        <>
          {err ? <div className="rm-error">{err}</div> : null}
          {loading ? <div className="rm-loading">Cargando…</div> : null}

          {/* TABLA: Resumen */}
      <div className="rm-card rm-t1">
        <div className="rm-card-title">Resumen</div>
        <div className="rm-table">
          <div className="rm-thead">
            <div>Concepto</div>
            <div className="rm-right">Total</div>
          </div>

          {resumen.rows.map((row, i) => (
            <div className="rm-tr" key={i}>
              <div className="rm-bold">{row.concepto}</div>
              <div className="rm-right rm-bold">{fmt(row.total)}</div>
            </div>
          ))}

          <div className="rm-tfoot">
            <div>TOTAL FINAL</div>
            <div className="rm-right">{fmt(resumen.totalFinal)}</div>
          </div>
        </div>
      </div>

      
{/* NUEVA TABLA: Totales por Project (Main SFD + Coax Bentley SFD) */}
<div className="rm-card rm-t2">
  <div className="rm-card-title">Totales Main SFD + Coax Bentley SFD</div>
  <div className="rm-table">
    <div className="rm-thead">
      <div>Project</div>
      <div className="rm-right">Total</div>
    </div>

    {totalesSFDPorProject.rows.map((row, i) => (
      <div className="rm-tr" key={i}>
        <div className="rm-bold">{row.jobpro}</div>
        <div className="rm-right rm-bold">{fmt(row.total)}</div>
      </div>
    ))}

    <div className="rm-tfoot">
      <div>TOTAL</div>
      <div className="rm-right">{fmt(totalesSFDPorProject.grandTotal)}</div>
    </div>
  </div>
</div>

      {/* TABLA: Total final por día */}
      <div className="rm-card rm-t3">
        <div className="rm-card-title">TOTAL FINAL por día</div>
        <div className="rm-table">
          <div className="rm-thead">
            <div>Día</div>
            <div className="rm-right">Total</div>
          </div>

          {porDia.rows.map((row, i) => (
            <div className="rm-tr" key={i}>
              <div className="rm-bold">{row.day}</div>
              <div className="rm-right rm-bold">{fmt(row.total)}</div>
            </div>
          ))}

          <div className="rm-tfoot">
            <div>TOTAL</div>
            <div className="rm-right">{fmt(porDia.grandTotal)}</div>
          </div>
        </div>
      </div>

      {/* Fecha a procesar (antes de la 3ra tabla) */}
      <div
        className="rm-dateRow"
        style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 12px" }}
      >
        <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>Fecha a procesar:</div>
        <input
          className="rm-date"
          type="date"
          value={processDate}
          onChange={(e) => setProcessDate(e.target.value)}
        />
      </div>

      {/* TABLA: Por Project (fecha) */}
      <div className="rm-card rm-t4">
        <div className="rm-card-title">Por Project ({porProject.dateKey})</div>

        <div className="rm-table">
          <div className="rm-thead rm-3">
            <div>Project</div>
            <div className="rm-right">Registros</div>
            <div className="rm-right">Total</div>
          </div>

          {porProject.rows.map((row, i) => (
            <div className="rm-tr rm-3" key={i}>
              <div className="rm-bold">{row.jobpro}</div>
              <div className="rm-right rm-bold">{row.count}</div>
              <div className="rm-right rm-bold">{fmt(row.total)}</div>
            </div>
          ))}

          <div className="rm-tfoot rm-3">
            <div>TOTAL HOY</div>
            <div className="rm-right">{porProject.dayCount}</div>
            <div className="rm-right">{fmt(porProject.dayTotal)}</div>
          </div>
        </div>
      </div>
        </>
      )}
      </div>
    </div>
  );
}
